import { Typography as MuiTypography } from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { z } from "zod";

import useQueryParams from "../../hooks/useQueryParams";
import FiltersForm from "../../components/FiltersForm";
import InflowComparisonPairContainer from "../../components/charts/inflowComparisonPair/InflowComparisonPairContainer";
import React from "react";
import { usePostHeight } from "../../hooks/usePostHeight";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);

const PARAMETER_GROUPS = [
  { label: "Total Nitrogen", value: 29 },
  { label: "Total Phosphorus", value: 28 },
  { label: "Total Suspended Solids", value: 30 },
];

const FLOW_OPTIONS = [
  { label: "All Flow", value: 3 },
  { label: "Base Flow", value: 2 },
  { label: "Storm Flow", value: 1 },
];

const CURRENT_YEAR = new Date().getFullYear();

const YEARS_2015_TO_NOW = Array.from(
  { length: CURRENT_YEAR - 2015 + 1 },
  (_, i) => (2015 + i).toString()
);

const DEFAULTS = {
  parameterid: 29,
  flowcond: 3,
  scaletype: "fixed",
  years: [CURRENT_YEAR - 1, CURRENT_YEAR].map(String),
};

const schema = z.object({
  parameterid: z.coerce.number().default(DEFAULTS.parameterid),
  flowcond: z.coerce.number().default(DEFAULTS.flowcond),
  scaletype: z.enum(["fixed", "dynamic"]).default(DEFAULTS.scaletype),
  years: z
    .preprocess(
      (v) => (typeof v === "string" ? v.split(",") : v),
      z.array(z.string())
    )
    .default(DEFAULTS.years),
  controls: z.preprocess(
    (val) => val === "true" || val === true,
    z.boolean().default(false)
  ),
  height: z.preprocess(
    (val) => (typeof val === "string" ? parseInt(val, 10) : val),
    z.number().default(400)
  ),
});

export default function WqInflowComparisonEmbed() {
  usePostHeight();
  const { params, setParams } = useQueryParams(schema);

  const getParameterLabel = () => {
    const param = PARAMETER_GROUPS.find((p) => p.value === params.parameterid);
    return param ? param.label : "Water Quality Parameter";
  };

  const FILTERS = [
    {
      type: "select",
      param: "parameterid",
      label: "Parameter Group",
      default: String(params.parameterid),
      options: PARAMETER_GROUPS.map(({ label, value }) => ({
        label,
        value: String(value),
      })),
    },
    {
      type: "select",
      param: "flowcond",
      label: "Flow Conditions",
      default: String(params.flowcond),
      options: FLOW_OPTIONS.map(({ label, value }) => ({
        label,
        value: String(value),
      })),
    },
  ];

  const handleYearChange = (yrs) => setParams({ ...params, years: yrs });
  const handleScaleChange = (newScale) =>
    setParams({ ...params, scaletype: newScale });

  return (
    <>
      <main>
        {params.controls && (
          <Section>
            <Typography variant="h5" component="h2" gutterBottom>
              Filters
            </Typography>
            <FiltersForm config={FILTERS} onChange={setParams} />
          </Section>
        )}

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Monthly {getParameterLabel()} Concentrations by Year
          </Typography>
          <InflowComparisonPairContainer
            parameterid={params.parameterid}
            years={params.years}
            allYears={YEARS_2015_TO_NOW}
            flowcond={params.flowcond}
            scaletype={params.scaletype}
            onYearsChange={handleYearChange}
            onScaleChange={handleScaleChange}
            height={params.height}
          />
        </Section>
      </main>
    </>
  );
}
